import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/** Mirrors G2ReviewCreditStatus in
 *  cleanse_api/models/billing/g2_review_credit.py. */
export type G2ReviewCreditStatus = 'pending' | 'approved' | 'rejected';

/** Mirrors G2ReviewCreditResponse in
 *  cleanse_api/api/billing/g2_review_credits.py. */
export interface G2ReviewCreditRequest {
  id: string;
  organization_id: string;
  user_id: string;
  review_url: string;
  status: G2ReviewCreditStatus;
  credits_awarded: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string | null;
}

/** Mirrors G2ReviewCreditApproveResponse. */
export interface G2ReviewCreditApproveResponse {
  request: G2ReviewCreditRequest;
  credits_granted: number;
  new_balance: number;
}

export const g2ReviewCreditsApi = createApi({
  reducerPath: 'g2ReviewCreditsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['G2ReviewCredit'],
  endpoints: (builder) => ({
    // The caller's latest claim, from any workspace (one reward per person);
    // null if they never sent one.
    getMyG2ReviewCredit: builder.query<G2ReviewCreditRequest | null, void>({
      query: () => '/g2-review-credits/me',
      providesTags: [{ type: 'G2ReviewCredit', id: 'MINE' }],
    }),

    // Send a review link. 409 if the caller already has a pending/approved
    // claim; 400 if the URL isn't a Cleanlist AI review on G2.
    submitG2ReviewCredit: builder.mutation<
      G2ReviewCreditRequest,
      { review_url: string }
    >({
      query: (body) => ({
        url: '/g2-review-credits',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'G2ReviewCredit', id: 'MINE' }],
    }),

    // Staff-only (server gates on @cleanlist.ai → 403 otherwise).
    adminListPendingG2ReviewCredits: builder.query<
      G2ReviewCreditRequest[],
      { limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/g2-review-credits/pending',
        params: {
          limit: args?.limit ?? 50,
          offset: args?.offset ?? 0,
        },
      }),
      providesTags: [{ type: 'G2ReviewCredit', id: 'PENDING' }],
    }),

    adminApproveG2ReviewCredit: builder.mutation<
      G2ReviewCreditApproveResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/g2-review-credits/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'G2ReviewCredit', id: 'PENDING' }],
    }),

    adminRejectG2ReviewCredit: builder.mutation<
      G2ReviewCreditRequest,
      { id: string; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/g2-review-credits/${id}/reject`,
        method: 'POST',
        body: { reason: reason || null },
      }),
      invalidatesTags: [{ type: 'G2ReviewCredit', id: 'PENDING' }],
    }),
  }),
});

export const {
  useGetMyG2ReviewCreditQuery,
  useSubmitG2ReviewCreditMutation,
  useAdminListPendingG2ReviewCreditsQuery,
  useAdminApproveG2ReviewCreditMutation,
  useAdminRejectG2ReviewCreditMutation,
} = g2ReviewCreditsApi;
